/**
 * Cache Configuration Registry
 *
 * Centralized configuration for all cached endpoints.
 * Each entry defines TTL, auto-refresh behavior, and invalidation events.
 */

export type CacheStorage = 'indexeddb' | 'memory'

export interface CacheConfig {
  ttl: number
  storage?: CacheStorage
  autoRefresh?: boolean
  checkInterval?: number | null
  events?: string[]
  staleWhileRevalidate?: boolean
  description?: string
}

export const CACHE_DEFAULTS: CacheConfig = {
  ttl: 30 * 60 * 1000,
  storage: 'indexeddb',
  autoRefresh: false,
  checkInterval: null,
  events: [],
  staleWhileRevalidate: true
}

export const CACHE_CONFIGS: Record<string, Partial<CacheConfig>> = {
  formasPagamento: {
    ttl: 60 * 60 * 1000,
    autoRefresh: false,
    events: ['formasPagamento.updated'],
    description: 'Payment methods list'
  },

  categoriasDespesas: {
    ttl: 60 * 60 * 1000,
    autoRefresh: false,
    events: ['categoriasDespesas.updated'],
    description: 'Expense categories list'
  },

  'translations.*': {
    ttl: 4 * 60 * 60 * 1000,
    autoRefresh: false,
    events: ['translations.updated'],
    description: 'i18n translations by locale'
  },

  // `userFilter` NÃO tem entrada aqui de propósito: desde 2026-09-01 o filtro
  // é durável no backend (filtros_usuarios) e não é cacheado no navegador.

  // `permissions` NÃO tem entrada aqui de propósito: autorização não é
  // persistida no cliente desde 2026-09-01 (plano auth-state-jwt-like).

  usuarios: {
    ttl: 10 * 60 * 1000,
    storage: 'memory',
    autoRefresh: false,
    events: ['usuarios.updated'],
    description: 'Users list for dropdowns'
  },

  'dashboard.*': {
    ttl: 5 * 60 * 1000,
    storage: 'memory',
    autoRefresh: false,
    events: ['dashboard.refresh'],
    description: 'Dashboard chart data and statistics'
  },

  'documentConfig.*': {
    ttl: 60 * 60 * 1000,
    storage: 'indexeddb',
    autoRefresh: false,
    events: [],
    description: 'Document type config by franchise country'
  }
}

export function getCacheConfig(key: string): CacheConfig {
  if (CACHE_CONFIGS[key]) {
    return { ...CACHE_DEFAULTS, ...CACHE_CONFIGS[key] }
  }

  for (const [configKey, config] of Object.entries(CACHE_CONFIGS)) {
    if (configKey.includes('*')) {
      const regex = new RegExp('^' + configKey.replace(/\*/g, '.*') + '$')
      if (regex.test(key)) {
        return { ...CACHE_DEFAULTS, ...config }
      }
    }
  }

  return { ...CACHE_DEFAULTS }
}

export interface CacheConfigWithKey extends CacheConfig {
  key: string
}

export function listCacheConfigs(): CacheConfigWithKey[] {
  return Object.entries(CACHE_CONFIGS).map(([key, config]) => ({
    key,
    ...CACHE_DEFAULTS,
    ...config
  }))
}
